// src/pages/LMS/Courses/CourseEnrollments.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Chip, Avatar, Stack, IconButton,
  LinearProgress, CircularProgress, Checkbox
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const CourseEnrollments = () => {
  const { chatbotId, courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [unenrollDialog, setUnenrollDialog] = useState({ open: false, enrollment: null });

  useEffect(() => {
    loadCourseData();
    loadEnrollments();
  }, [courseId]);

  const loadCourseData = async () => {
    try {
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}`);
      setCourse(response.data.data);
    } catch (error) {
      console.error('Error loading course:', error);
    }
  };

  const loadEnrollments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const response = await axiosInstance.get(`/api/lms/courses/${courseId}/enrollments`, { params });
      setEnrollments(response.data.data);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableStudents = async () => {
    try {
      // Get students who are NOT enrolled in this course
      const response = await axiosInstance.get('/api/lms/students', {
        params: { not_enrolled_in: courseId, status: 'active' }
      });
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const handleEnrollStudents = async () => {
    if (selectedStudents.length === 0) {
      alert('Please select at least one student');
      return;
    }

    try {
      await axiosInstance.post(`/api/lms/courses/${courseId}/bulk-enroll`, {
        student_ids: selectedStudents
      });
      
      alert(`Successfully enrolled ${selectedStudents.length} student(s)`);
      setEnrollDialog(false);
      setSelectedStudents([]);
      loadEnrollments();
    } catch (error) {
      console.error('Error enrolling students:', error);
      alert('Error enrolling students');
    }
  };

  const handleUnenroll = async () => {
    if (!unenrollDialog.enrollment) return;

    try {
      await axiosInstance.post(`/api/lms/enrollments/${unenrollDialog.enrollment.id}/unenroll`);
      alert('Student unenrolled successfully');
      setUnenrollDialog({ open: false, enrollment: null });
      loadEnrollments();
    } catch (error) {
      console.error('Error unenrolling student:', error);
      alert('Error unenrolling student');
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      enrolled: 'default',
      in_progress: 'info',
      completed: 'success',
      dropped: 'error'
    };
    return <Chip label={status.replace('_', ' ').toUpperCase()} color={colors[status]} size="small" />;
  };

  if (loading && !course) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}>
            <BackIcon />
          </IconButton>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              Course Enrollments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {course?.title}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            loadAvailableStudents();
            setEnrollDialog(true);
          }}
        >
          Enroll Students
        </Button>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="primary" fontWeight={700}>
            {enrollments.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Enrollments
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="info.main" fontWeight={700}>
            {enrollments.filter(e => e.status === 'in_progress').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            In Progress
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="success.main" fontWeight={700}>
            {enrollments.filter(e => e.status === 'completed').length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completed
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="h4" color="warning.main" fontWeight={700}>
            {enrollments.length > 0 
              ? ((enrollments.filter(e => e.status === 'completed').length / enrollments.length) * 100).toFixed(0)
              : 0}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completion Rate
          </Typography>
        </Paper>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="enrolled">Enrolled</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="dropped">Dropped</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={loadEnrollments}>
            Apply Filters
          </Button>
        </Stack>
      </Paper>

      {/* Enrollments Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Identity</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Enrolled Date</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : enrollments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No students enrolled yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              enrollments.map((enrollment) => (
                <TableRow key={enrollment.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {enrollment.student?.first_name?.[0]}
                        {enrollment.student?.last_name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {enrollment.student?.first_name} {enrollment.student?.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {enrollment.student?.department || 'No department'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{enrollment.student?.student_identity}</TableCell>
                  <TableCell>{enrollment.student?.email}</TableCell>
                  <TableCell>
                    <Box sx={{ width: 120 }}>
                      <Typography variant="caption" display="block">
                        {enrollment.progress_percentage?.toFixed(0) || 0}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={enrollment.progress_percentage || 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(enrollment.status)}</TableCell>
                  <TableCell>
                    {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {enrollment.last_accessed_at
                      ? new Date(enrollment.last_accessed_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/chatbot/${chatbotId}/lms/enrollments/${enrollment.enrollment_identity}/progress`)}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setUnenrollDialog({ open: true, enrollment })}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Enroll Dialog */}
      <Dialog open={enrollDialog} onClose={() => setEnrollDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Enroll Students in {course?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select students to enroll in this course
          </Typography>
          
          {students.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No available students to enroll. All active students are already enrolled.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudents.length === students.length}
                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents(students.map(s => s.id));
                          } else {
                            setSelectedStudents([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Department</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedStudents.includes(student.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStudents([...selectedStudents, student.id]);
                            } else {
                              setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {student.first_name} {student.last_name}
                      </TableCell>
                      <TableCell>{student.email}</TableCell>
                      <TableCell>{student.department || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEnrollStudents}
            disabled={selectedStudents.length === 0}
          >
            Enroll {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unenroll Dialog */}
      <Dialog open={unenrollDialog.open} onClose={() => setUnenrollDialog({ open: false, enrollment: null })}>
        <DialogTitle>Unenroll Student</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unenroll{' '}
            {unenrollDialog.enrollment?.student?.first_name}{' '}
            {unenrollDialog.enrollment?.student?.last_name} from this course?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Their progress will be preserved if they re-enroll later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnenrollDialog({ open: false, enrollment: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleUnenroll}>
            Unenroll
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseEnrollments;